import React, { useCallback, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import parse, { domToReact } from "html-react-parser";
import Text from "./Text";
import useGlossaryHandler from "../../hooks/useGlossaryHandler";
import { stripUrlReferenceTags, parseInlineStyles } from "../../utils/index";
import { PageStateContext, ChapterContext } from "../../PageContext";

/**
 * Ensure an href has a scheme.
 *
 * CKEditor (and Craft's own editor preview) treats URLs without a scheme as
 * relative paths. For bare domain names like "myDCI.digital-control.com" this
 * produces garbage URLs (e.g. /manuals/123/site/1/myDCI.digital-control.com).
 * These are content authoring errors — editors should always include "https://".
 * We recover defensively by prepending "https://" to anything that looks like a
 * bare hostname. Using "/" would wrongly serve the domain off our own origin;
 * leaving it as-is reproduces the same broken relative-path behaviour as the browser.
 *
 * Rules:
 *  - Already has any scheme (RFC 3986) / protocol-relative / root-relative / hash → return as-is
 *  - Looks like a bare hostname (contains ".", doesn't start with ".." or ".") → prepend "https://"
 */
function normalizeHref(url) {
  if (!url) return url;
  // Already has any scheme (RFC 3986: letter followed by letters/digits/+/-/. then colon)
  if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(url)) return url;
  // Protocol-relative, root-relative, or hash
  if (url.startsWith("//") || url.startsWith("/") || url.startsWith("#")) return url;
  // Schemeless bare hostname: dot in the first segment (before any "/") means
  // this is a domain name, not a relative path with a dotted filename/extension.
  const firstSegment = url.split("/")[0];
  if (firstSegment.includes(".") && !firstSegment.startsWith(".")) {
    return `https://${url}`;
  }
  return url;
}

/**
 * Detect Craft CMS internal content links and map them to app-relative /link/ URLs.
 * Handles /digiguide/manuals/, /digiguide/chapters/, /digiguide/topics/ patterns.
 * Returns { type, id } or null.
 */
function extractCraftInternalLink(href) {
  if (!href) return null;
  // Strip ref tags first so we match the actual URL, not the raw {entry:...|fallback} string.
  let url = stripUrlReferenceTags(href);
  let decoded = url;
  for (let i = 0; i < 2; i += 1) {
    try {
      const tmp = decodeURIComponent(decoded);
      if (tmp === decoded) break;
      decoded = tmp;
    } catch { break; }
  }
  if (decoded.includes("?")) decoded = decoded.split("?")[0];

  let m;
  m = decoded.match(/\/digiguide\/manuals?\/(\d+)/i);
  if (m) return { type: "Manual", id: m[1] };

  m = decoded.match(/\/digiguide\/chapters?\/(\d+)/i);
  if (m) return { type: "Chapter", id: m[1] };

  m = decoded.match(/\/digiguide\/topics?\/(\d+)/i);
  if (m) return { type: "TopicArticle", id: m[1] };

  return null;
}

const RichText = ({
  html,
  variant = "body",
  className = "",
  style = {},
  handleAnchor = null,
  fullWidth = false,
  isNested = false,
}) => {
  const textOptions = variant ? { size: `text-${variant}` } : {};
  const { isValidTermId, extractTermIdFromHref } = useGlossaryHandler(); // don't import handleGlossaryAnchor from here, need to use instance passed down via props
  const { pageState } = useContext(PageStateContext);
  const chapterId = useContext(ChapterContext);
  const manualId = pageState?.manualId || null;

  // Use useEffect to process the HTML and collect glossaryIds
  useEffect(() => {
    if (handleAnchor) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const anchorTags = tempDiv.querySelectorAll("a");
      anchorTags.forEach(anchor => {
        const href = anchor.getAttribute("href");
        handleAnchor(href);
      });
    }
  }, [html, handleAnchor]);

  const options = {
    replace: useCallback(
      ({ name, attribs, next, prev, children }) => {
        const marginClass = classNames({
          "mt-4 text-prev": !!prev,
          "mb-4 text-next": !fullWidth && !!next,
          "break-before-avoid": !prev,
        });

        // Use styled lists
        if (name === "ul") {
          return (
            <ul
              className={`list-reset ml-5 break-before-avoid list-disc ${fullWidth} ${
                !fullWidth && fullWidth !== false ? marginClass : ""
              }`}
            >
              {domToReact(children, options)}
            </ul>
          );
        }

        if (name === "ol") {
          return (
            <ol
              className={`list-reset ml-5 list-decimal break-before-avoid ${fullWidth} ${
                !fullWidth && fullWidth !== false ? marginClass : ""
              }`}
            >
              {domToReact(children, options)}
            </ol>
          );
        }

        if (name === "li") {
          return (
            <li className="dont-split">
              <Text
                tag="span"
                use="p"
                className={`pl-2 item ${fullWidth} ${
                  !fullWidth && fullWidth !== false ? marginClass : ""
                }`}
                options={textOptions}
              >
                {domToReact(children, options)}
              </Text>
            </li>
          );
        }

        // Render <a> tags differently based on whether they are glossary terms or varaible anchors
        if (name === "a") {
          /* ------------------------------------------------------------------
           * 1.  **Preserve variable-reference links** (contain `var-ref`,
           *     `var-missing`, or `.tooltip`) so the tooltip CSS keeps working.
           * ---------------------------------------------------------------- */
          const classes = attribs?.class || "";
          const isVariableAnchor =
            classes.includes("var-ref") ||
            classes.includes("var-missing") ||
            classes.includes("tooltip") || // future-proof
            (attribs?.href || "").startsWith("variables-scoped/");

          if (isVariableAnchor) {
            /* Return the element untouched (except for React’s `className`).   */
            return React.createElement(
              "a",
              {
                ...attribs,
                className: classes, // keep ‘var-ref tooltip’ etc.
              },
              domToReact(children, options)
            );
          }

          /* ------------------------------------------------------------------
           * 2.  glossary-term, Craft internal links, and normal-link handling
           * ---------------------------------------------------------------- */

          const href = attribs && attribs.href ? attribs.href : "";
          const termId = extractTermIdFromHref(href);

          // Render as glossary term if:
          //   a) the ID is known in globals, OR
          //   b) the href clearly points to a glossary URL (even if ID not in globals)
          // We decode one level so URI-encoded ref tags with /glossary/ in their
          // fallback URL are also caught. We do NOT treat every {entry:} ref tag as
          // a glossary hit — extractTermIdFromHref can return an ID for any
          // URI-encoded entry ref tag when originallyEncoded is true.
          let decodedHref = href;
          try { decodedHref = decodeURIComponent(decodedHref); } catch { /* noop */ }
          const isGlossary = isValidTermId(termId) ||
            (termId && /\/(?:digiguide\/)?glossary\/\d+/i.test(decodedHref));

          if (isGlossary) {
            return (
              <Text
                tag="span"
                use="p"
                options={{
                  font: "font-highlight",
                  style: "inline",
                  size: textOptions.size,
                }}
                className="relative pr-2 rich-text"
                preserveWidows
              >
                <span className="border-b-2 border-blue-accent">
                  {domToReact(children, options)} *
                </span>
              </Text>
            );
          }

          // Convert Craft CMS internal content links to app-relative /link/ URLs.
          const craftLink = extractCraftInternalLink(href);
          if (craftLink) {
            let craftHref;
            if (craftLink.type === "Manual") {
              craftHref = `/link/Manual?id=${craftLink.id}`;
            } else if (craftLink.type === "Chapter") {
              craftHref = `/link/Chapter?id=${craftLink.id}${manualId ? `&manualId=${manualId}` : ""}`;
            } else {
              // TopicArticle
              craftHref = `/link/TopicArticle?id=${craftLink.id}${chapterId ? `&chapterId=${chapterId}` : ""}${manualId ? `&manualId=${manualId}` : ""}`;
            }
            return (
              <a href={craftHref} className="text-blue-500 border-blue-accent border-b-2">
                {domToReact(children, options)}
              </a>
            );
          }

          const cleanHref = stripUrlReferenceTags(href);
          // Render a normal <a> tag for external links
          return (
            <a
              href={cleanHref}
              className="text-blue-500 border-blue-accent border-b-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              {domToReact(children, options)}
            </a>
          );
        }

        // 3. Tables, Table Headers, Table Cells
        if (name === "table") {
          // Keep any class names from CKEditor markup (e.g. "table-font-size-9")
          const tableClass = classNames(
            `${attribs.class} w-max max-w-contentmaxmargin overflow-x-auto`
          ); // preserve any user-specified classes, max width is full width less margins, and overflow-x-auto lets us have a horizontal scroll to override max-w-content parent div

          return (
            <table className={tableClass} style={{ pageBreakInside: "avoid" }}>
              {domToReact(children, options)}
            </table>
          );
        }

        if (name === "th") {
          // Gather relevant table attributes
          const { class: classAttr, colspan, rowspan, style: inlineStyle } =
            attribs || {};

          const thClass = classNames(classAttr || "");

          const defaultStyle = {};
          let mergedStyle = { ...defaultStyle };
          if (inlineStyle) {
            const parsed = parseInlineStyles(inlineStyle);
            mergedStyle = { ...mergedStyle, ...parsed };
          }

          // Convert string colspan to a number if needed
          const colSpanNum = colspan ? parseInt(colspan, 10) : undefined;
          const rowSpanNum = rowspan ? parseInt(rowspan, 10) : undefined;

          return (
            <th
              className={thClass}
              colSpan={colSpanNum}
              rowSpan={rowSpanNum}
              style={mergedStyle}
            >
              {domToReact(children, options)}
            </th>
          );
        }

        if (name === "td") {
          const { class: classAttr, colspan, rowspan, style: inlineStyle } =
            attribs || {};

          const tdClass = classNames(classAttr || "");

          const defaultStyle = {};
          let mergedStyle = { ...defaultStyle };
          if (inlineStyle) {
            const parsed = parseInlineStyles(inlineStyle);
            mergedStyle = { ...mergedStyle, ...parsed };
          }

          const colSpanNum = colspan ? parseInt(colspan, 10) : undefined;
          const rowSpanNum = rowspan ? parseInt(rowspan, 10) : undefined;

          return (
            <td
              className={tdClass}
              colSpan={colSpanNum}
              rowSpan={rowSpanNum}
              style={mergedStyle}
            >
              {domToReact(children, options)}
            </td>
          );
        }

        /* 0.  Keep variable wrappers intact  --------------------------- */
        if (
          name === "span" &&
          attribs &&
          (attribs.class || "").includes("var-")
        ) {
          /*  just hand the element back untouched except for
              turning its children into React nodes                   */
          return React.createElement(
            "span",
            {
              className: attribs.class,
              "data-var-id": attribs["data-var-id"],
            },
            domToReact(children, options)
          );
        }

        /* 0-bis.  Keep tooltip text spans for variable previews intact ------------------------- */
        if (name === "span" && attribs?.class?.includes("tooltiptext")) {
          return React.createElement(
            "span",
            { className: attribs.class },
            domToReact(children, options)
          );
        }

        /* needed to strip Url reference tags from inline images */
        if (name === "img") {
          /* <--  NEW branch – place above default handler */
          const cleanSrc = stripUrlReferenceTags(attribs?.src || "");
          return React.createElement("img", {
            ...attribs,
            src: cleanSrc,
            className: classNames(attribs?.class, "rt-inline-img"), // helper for CSS
            width: undefined, // drop hard-coded W/H
            height: undefined,
          });
        }

        // Render everything else using default styles.
        if (name && !["img", "br"].includes(name)) {
          return (
            <Text
              tag={name}
              className={`${marginClass} ${className} break-within-avoid`}
              preserveWidows
              options={textOptions}
            >
              {domToReact(children, options)}
            </Text>
          );
        }

        return null;
      },
      [textOptions, isValidTermId, extractTermIdFromHref, manualId, chapterId]
    ),
  };

  if (typeof html !== "string") {
    return null;
  }

  const parsedHtml = parse(html, options);

  return (
    <div className={className} style={style}>
      {parsedHtml}
    </div>
  );
};

RichText.propTypes = {
  html: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["sm", "body"]),
  className: PropTypes.string,
  style: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  handleAnchor: PropTypes.func,
  fullWidth: PropTypes.bool,
  isNested: PropTypes.bool,
};

export default RichText;
